// src/App.jsx — complete router with all 70 pages wired
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './context/AuthContext'
import { CartProvider } from './context/CartContext'
import { TenantProvider } from './context/TenantContext'

// Layouts
import PublicLayout   from './components/layout/PublicLayout'
import AuthLayout     from './components/layout/AuthLayout'
import DashboardLayout from './components/layout/DashboardLayout'

// Auth
import LoginPage        from './pages/auth/LoginPage'
import RegisterPage     from './pages/auth/RegisterPage'
import OtpVerifyPage    from './pages/auth/OtpVerifyPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage  from './pages/auth/ResetPasswordPage'
import SocialCallbackPage from './pages/auth/SocialCallbackPage'

// Public
import HomePage       from './pages/public/HomePage'
import CataloguePage  from './pages/public/CataloguePage'
import ProductPage    from './pages/public/ProductPage'
import SearchPage     from './pages/public/SearchPage'
import ComparePage    from './pages/public/ComparePage'
import SuppliersPage  from './pages/public/SuppliersPage'
import StorefrontPage from './pages/public/StorefrontPage'
import BlogPage       from './pages/public/BlogPage'
import RoadmapPage    from './pages/public/RoadmapPage'
import BlogPostPage   from './pages/public/BlogPostPage'
import CmsPage        from './pages/public/CmsPage'
import FaqPage        from './pages/public/FaqPage'

// Buyer
import BuyerDashboard    from './pages/buyer/BuyerDashboard'
import CartPage          from './pages/buyer/CartPage'
import CheckoutPage      from './pages/buyer/CheckoutPage'
import CheckoutSuccess   from './pages/buyer/CheckoutSuccess'
import OrdersPage        from './pages/buyer/OrdersPage'
import OrderDetailPage   from './pages/buyer/OrderDetailPage'
import WishlistPage      from './pages/buyer/WishlistPage'
import NotificationsPage from './pages/buyer/NotificationsPage'
import LoyaltyPage       from './pages/buyer/LoyaltyPage'
import ReferralsPage     from './pages/buyer/ReferralsPage'
import GiftCardsPage     from './pages/buyer/GiftCardsPage'
import RfqPage           from './pages/buyer/RfqPage'
import RfqDetailPage     from './pages/buyer/RfqDetailPage'
import GstCreditsPage    from './pages/buyer/GstCreditsPage'
import ReturnsPage       from './pages/buyer/ReturnsPage'
import SupportPage       from './pages/buyer/SupportPage'
import SamplesPage       from './pages/buyer/SamplesPage'
import MessagesPage      from './pages/buyer/MessagesPage'
import ProfilePage       from './pages/buyer/ProfilePage'
import AddressesPage     from './pages/buyer/AddressesPage'

// Supplier
import SupplierDashboard    from './pages/supplier/SupplierDashboard'
import ErpDashboard         from './pages/supplier/ErpDashboard'
import SmartPricingPage     from './pages/supplier/SmartPricingPage'
import ForecastDashboard    from './pages/supplier/ForecastDashboard'
import ProductsPage         from './pages/supplier/ProductsPage'
import ProductForm          from './pages/supplier/ProductForm'
import SupplierOrdersPage   from './pages/supplier/OrdersPage'
import SupplierOrderDetail  from './pages/supplier/OrderDetail'
import AnalyticsPage        from './pages/supplier/AnalyticsPage'
import PayoutsPage          from './pages/supplier/PayoutsPage'
import SupplierStorefront   from './pages/supplier/StorefrontPage'
import SupplierRfqPage      from './pages/supplier/RfqPage'
import SupplierSamplesPage  from './pages/supplier/SamplesPage'
import SupplierMessagesPage from './pages/supplier/MessagesPage'
import SubscriptionPage     from './pages/supplier/SubscriptionPage'
import BulkImportPage       from './pages/supplier/BulkImportPage'
import SupplierProfilePage  from './pages/supplier/ProfilePage'

// Admin
import AdminDashboard      from './pages/admin/AdminDashboard'
import UsersPage           from './pages/admin/UsersPage'
import UserDetail          from './pages/admin/UserDetail'
import AdminProductsPage   from './pages/admin/ProductsPage'
import AdminOrdersPage     from './pages/admin/OrdersPage'
import AdminOrderDetail    from './pages/admin/OrderDetail'
import CouponsPage         from './pages/admin/CouponsPage'
import AdminPayoutsPage    from './pages/admin/PayoutsPage'
import AdminAnalyticsPage  from './pages/admin/AnalyticsPage'
import HealthPage          from './pages/admin/HealthPage'
import FraudPage           from './pages/admin/FraudPage'
import FraudDetail         from './pages/admin/FraudDetail'
import DisputesPage        from './pages/admin/DisputesPage'
import DisputeDetail       from './pages/admin/DisputeDetail'
import AdminLoyaltyPage    from './pages/admin/LoyaltyPage'
import AdminGiftCardsPage  from './pages/admin/GiftCardsPage'
import AdminReferralsPage  from './pages/admin/ReferralsPage'
import AbandonedCartsPage  from './pages/admin/AbandonedCartsPage'
import SubscriptionsPage   from './pages/admin/SubscriptionsPage'
import PromotionsPage      from './pages/admin/PromotionsPage'
import AdminCmsPage        from './pages/admin/CmsPage'
import AnnouncementsPage   from './pages/admin/AnnouncementsPage'
import AuditPage           from './pages/admin/AuditPage'
import ImpersonationPage   from './pages/admin/ImpersonationPage'
import TenantAdminLayout   from './components/layout/TenantAdminLayout'
import { TenantDashboard, TenantBranding, TenantDomains, TenantTeam, TenantBilling } from './pages/tenant/TenantAdminPages'
import { TenantsPage, TenantProvision, TenantDetail } from './pages/admin/TenantPages'
import CompliancePage           from './pages/admin/CompliancePage'
import AdminReturnsPage        from './pages/admin/ReturnsPage'
import AdminTicketsPage        from './pages/admin/TicketsPage'
import CommissionsPage          from './pages/admin/CommissionsPage'
import SupplierVerificationPage from './pages/admin/SupplierVerificationPage'  // M1
import MarketIntelligencePage  from './pages/supplier/MarketIntelligencePage'  // M2
import PersonalizationPage    from './pages/buyer/PersonalizationPage'          // M2
import ReorderDashboard       from './pages/buyer/ReorderDashboard'            // M3
import AiChatWidget           from './components/ai/AiChatWidget'              // M3
import BuyerFinancePage       from './pages/buyer/BuyerFinancePage'            // M4
import InvoiceDiscountingPage from './pages/supplier/InvoiceDiscountingPage'   // M4
import AdminFinancePage       from './pages/admin/AdminFinancePage'             // M4
import WarehouseManagementPage from './pages/supplier/WarehouseManagementPage' // M5
import QualityInspectionPage   from './pages/supplier/QualityInspectionPage'   // M5
import ManufacturingOrderPage  from './pages/buyer/ManufacturingOrderPage'     // M5
import AdminLogisticsPage      from './pages/admin/AdminLogisticsPage'          // M5
import InternationalCheckoutPage from './pages/buyer/InternationalCheckoutPage'   // M6
import ExportCertificationPage   from './pages/supplier/ExportCertificationPage'  // M6
import AdminInternationalPage    from './pages/admin/AdminInternationalPage'       // M6
import AdminPlatformOsPage      from './pages/admin/AdminPlatformOsPage'           // M7
import NotificationInboxPage    from './pages/shared/NotificationInboxPage'        // M7
import TenantWebhookPage        from './pages/tenant/TenantWebhookPage'            // M7
import AdminGrowthPage          from './pages/admin/AdminGrowthPage'                // M8
import ReviewsPage              from './pages/buyer/ReviewsPage'                    // M8
import ReferralPage             from './pages/buyer/ReferralPage'                   // M8
import SupplierAnalyticsPage    from './pages/supplier/SupplierAnalyticsPage'       // M8
import BuyerTierPage          from './pages/buyer/BuyerTierPage'               // Feature 1
import GiMarketplacePage     from './pages/public/GiMarketplacePage'          // Feature 2
import SwatchSubscriptionPage from './pages/buyer/SwatchSubscriptionPage'     // Feature 3
import DesignAssistantPage   from './pages/buyer/DesignAssistantPage'         // Feature 4
import InsurancePage         from './pages/buyer/InsurancePage'               // Feature 5
import TextileIndexPage      from './pages/public/TextileIndexPage'           // Trend API
import LiveChatWidget         from './components/chat/LiveChatWidget'           // Phase 5
import AdminFaqsPage          from './pages/admin/AdminFaqsPage'                // Phase 6
import SalesReportPage        from './pages/admin/SalesReportPage'              // Phase 6
import GstReportPage          from './pages/admin/GstReportPage'                // Phase 6
import SupplierPerformancePage from './pages/admin/SupplierPerformancePage'     // Phase 6

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 1000 * 60 } } })

function RequireAuth({ children, role }) {
  const { user, loading } = useAuth()
  if (loading) return null
  if (!user) return <Navigate to="/login" replace />
  if (role && user.role !== role && user.role !== 'admin') return <Navigate to="/" replace />
  return children
}

function RedirectIfAuth({ children }) {
  const { user, loading } = useAuth()
  if (loading) return children          // show form while checking auth
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : user.role === 'supplier' ? '/supplier' : '/buyer'} replace />
  return children
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <TenantProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
            <Routes>
              {/* Auth */}
              <Route element={<AuthLayout />}>
                <Route path="/login"          element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
                <Route path="/register"       element={<RedirectIfAuth><RegisterPage /></RedirectIfAuth>} />
                <Route path="/otp-verify"     element={<OtpVerifyPage />} />
                <Route path="/forgot-password" element={<RedirectIfAuth><ForgotPasswordPage /></RedirectIfAuth>} />
                <Route path="/reset-password"  element={<RedirectIfAuth><ResetPasswordPage /></RedirectIfAuth>} />
                <Route path="/auth/social/:provider/callback" element={<SocialCallbackPage />} />
              </Route>

              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/"                   element={<HomePage />} />
                <Route path="/fabrics"             element={<CataloguePage />} />
                <Route path="/fabrics/:slug"       element={<ProductPage />} />
                <Route path="/search"              element={<SearchPage />} />
                <Route path="/compare"             element={<ComparePage />} />
                <Route path="/suppliers"           element={<SuppliersPage />} />
                <Route path="/suppliers/:slug"     element={<StorefrontPage />} />
                <Route path="/gi-marketplace"      element={<GiMarketplacePage />} />
                <Route path="/textile-index"       element={<TextileIndexPage />} />
                <Route path="/blog"                element={<BlogPage />} />
                <Route path="/roadmap"              element={<RoadmapPage />} />
                <Route path="/blog/:slug"          element={<BlogPostPage />} />
                <Route path="/faq"                 element={<FaqPage />} />
                <Route path="/cart"                element={<CartPage />} />
                <Route path="/checkout"            element={<CheckoutPage />} />
                <Route path="/:slug"               element={<CmsPage />} />
              </Route>

              {/* Buyer */}
              <Route path="/buyer" element={<RequireAuth role="buyer"><DashboardLayout role="buyer" /></RequireAuth>}>
                <Route index                      element={<BuyerDashboard />} />
                <Route path="orders"              element={<OrdersPage />} />
                <Route path="orders/success"      element={<CheckoutSuccess />} />
                <Route path="orders/:id"          element={<OrderDetailPage />} />
                <Route path="checkout"            element={<CheckoutPage />} />
                <Route path="wishlist"            element={<WishlistPage />} />
                <Route path="notifications"       element={<NotificationsPage />} />
                <Route path="loyalty"             element={<LoyaltyPage />} />
                <Route path="referrals"           element={<ReferralsPage />} />
                <Route path="gift-cards"          element={<GiftCardsPage />} />
                <Route path="rfq"                 element={<RfqPage />} />
                <Route path="rfq/:id"             element={<RfqDetailPage />} />
                <Route path="samples"             element={<SamplesPage />} />
                <Route path="messages"            element={<MessagesPage />} />
                <Route path="profile"             element={<ProfilePage />} />
                <Route path="addresses"           element={<AddressesPage />} />
                {/* M2 */}
                <Route path="recommendations"    element={<PersonalizationPage />} />
                <Route path="recently-viewed"    element={<PersonalizationPage />} />
                {/* M3 */}
                <Route path="reorder"            element={<ReorderDashboard />} />
                {/* M4 */}
                <Route path="finance"            element={<BuyerFinancePage />} />
                <Route path="manufacturing"      element={<ManufacturingOrderPage />} />  {/* M5 */}
                <Route path="international"      element={<InternationalCheckoutPage />} />  {/* M6 */}
                <Route path="reviews"            element={<ReviewsPage />} />                {/* M8 */}
                <Route path="referral"           element={<ReferralPage />} />               {/* M8 */}
                <Route path="tier"               element={<BuyerTierPage />} />              {/* Feature 1 */}
                <Route path="swatch-subscription" element={<SwatchSubscriptionPage />} />  {/* Feature 3 */}
                <Route path="design-assistant"   element={<DesignAssistantPage />} />       {/* Feature 4 */}
                <Route path="insurance"          element={<InsurancePage />} />              {/* Feature 5 */}
                <Route path="gst-credits"        element={<GstCreditsPage />} />             {/* Phase 3 */}
                <Route path="returns"            element={<ReturnsPage />} />               {/* Phase 4 */}
                <Route path="support"            element={<SupportPage />} />               {/* Phase 4 */}
              </Route>
              <Route path="/supplier" element={<RequireAuth role="supplier"><DashboardLayout role="supplier" /></RequireAuth>}>
                <Route index                       element={<SupplierDashboard />} />
                <Route path="erp"                  element={<ErpDashboard />} />
                <Route path="products"             element={<ProductsPage />} />
                <Route path="products/new"         element={<ProductForm />} />
                <Route path="products/:id/edit"    element={<ProductForm />} />
                <Route path="orders"               element={<SupplierOrdersPage />} />
                <Route path="orders/:id"           element={<SupplierOrderDetail />} />
                <Route path="analytics"            element={<AnalyticsPage />} />
                <Route path="pricing"              element={<SmartPricingPage />} />
                <Route path="forecast"             element={<ForecastDashboard />} />
                <Route path="payouts"              element={<PayoutsPage />} />
                <Route path="storefront"           element={<SupplierStorefront />} />
                <Route path="rfq"                  element={<SupplierRfqPage />} />
                <Route path="samples"              element={<SupplierSamplesPage />} />
                <Route path="messages"             element={<SupplierMessagesPage />} />
                <Route path="subscription"         element={<SubscriptionPage />} />
                <Route path="import"               element={<BulkImportPage />} />
                <Route path="profile"              element={<SupplierProfilePage />} />
                <Route path="market-intelligence"  element={<MarketIntelligencePage />} />  {/* M2 */}
                <Route path="finance"             element={<InvoiceDiscountingPage />} />  {/* M4 */}
                <Route path="warehouse"           element={<WarehouseManagementPage />} />  {/* M5 */}
                <Route path="quality"             element={<QualityInspectionPage />} />    {/* M5 */}
                <Route path="export-certs"       element={<ExportCertificationPage />} />  {/* M6 */}
                <Route path="notifications"      element={<NotificationInboxPage />} />      {/* M7 */}
                <Route path="webhooks"           element={<TenantWebhookPage />} />          {/* M7 */}
                <Route path="analytics"          element={<SupplierAnalyticsPage />} />      {/* M8 */}
              </Route>

              {/* Admin */}
              <Route path="/admin" element={<RequireAuth role="admin"><DashboardLayout role="admin" /></RequireAuth>}>
                <Route index                      element={<AdminDashboard />} />
                <Route path="users"               element={<UsersPage />} />
                <Route path="users/:id"           element={<UserDetail />} />
                <Route path="products"            element={<AdminProductsPage />} />
                <Route path="orders"              element={<AdminOrdersPage />} />
                <Route path="orders/:id"          element={<AdminOrderDetail />} />
                <Route path="coupons"             element={<CouponsPage />} />
                <Route path="payouts"             element={<AdminPayoutsPage />} />
                <Route path="analytics"           element={<AdminAnalyticsPage />} />
                <Route path="health"              element={<HealthPage />} />
                <Route path="fraud"               element={<FraudPage />} />
                <Route path="fraud/:userId"       element={<FraudDetail />} />
                <Route path="disputes"            element={<DisputesPage />} />
                <Route path="disputes/:id"        element={<DisputeDetail />} />
                <Route path="loyalty"             element={<AdminLoyaltyPage />} />
                <Route path="gift-cards"          element={<AdminGiftCardsPage />} />
                <Route path="referrals"           element={<AdminReferralsPage />} />
                <Route path="abandoned-carts"     element={<AbandonedCartsPage />} />
                <Route path="subscriptions"       element={<SubscriptionsPage />} />
                <Route path="promotions"          element={<PromotionsPage />} />
                <Route path="cms"                 element={<AdminCmsPage />} />
                <Route path="announcements"       element={<AnnouncementsPage />} />
                <Route path="audit"               element={<AuditPage />} />
                <Route path="commissions"         element={<CommissionsPage />} />
                <Route path="impersonate"         element={<ImpersonationPage />} />
                <Route path="suppliers/verification"  element={<SupplierVerificationPage />} />  {/* M1 */}
                <Route path="tenants"            element={<TenantsPage />} />
                <Route path="tenants/provision"  element={<TenantProvision />} />
                <Route path="tenants/:id"        element={<TenantDetail />} />
                <Route path="finance"            element={<AdminFinancePage />} />  {/* M4 */}
                <Route path="logistics"           element={<AdminLogisticsPage />} />       {/* M5 */}
                <Route path="international"      element={<AdminInternationalPage />} />   {/* M6 */}
                <Route path="platform-os"        element={<AdminPlatformOsPage />} />       {/* M7 */}
                <Route path="growth"             element={<AdminGrowthPage />} />            {/* M8 */}
                <Route path="compliance"         element={<CompliancePage />} />             {/* Phase 3 */}
                <Route path="returns"            element={<AdminReturnsPage />} />          {/* Phase 4 */}
                <Route path="tickets"            element={<AdminTicketsPage />} />          {/* Phase 4 */}
                <Route path="faqs"               element={<AdminFaqsPage />} />              {/* Phase 6 */}
                <Route path="reports/sales"      element={<SalesReportPage />} />            {/* Phase 6 */}
                <Route path="reports/gst"        element={<GstReportPage />} />              {/* Phase 6 */}
                <Route path="supplier-performance" element={<SupplierPerformancePage />} /> {/* Phase 6 */}
                <Route path="notifications"      element={<NotificationInboxPage />} />
              </Route>

              {/* Tenant Admin Portal */}
              <Route path="/tenant" element={<RequireAuth roles={['tenant_admin','tenant_manager']}><TenantAdminLayout /></RequireAuth>}>
                <Route path="dashboard" element={<TenantDashboard />} />
                <Route path="branding"  element={<TenantBranding />} />
                <Route path="domains"   element={<TenantDomains />} />
                <Route path="team"      element={<TenantTeam />} />
                <Route path="billing"   element={<TenantBilling />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <AiChatWidget />   {/* M3: floating AI assistant */}
            <LiveChatWidget /> {/* Phase 5: live buyer-supplier chat */}
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
      </TenantProvider>
    </QueryClientProvider>
  )
}
